import type { CaptureResult } from 'posthog-js'

import {
  containsOpaqueAnalyticsToken,
  sanitizeAnalyticsPath,
  sanitizePostHogEvent,
} from './posthog-privacy'

type PropertyBagContext = 'event' | 'person'

const EVENT_PII_PROPERTY_KEYS = new Set([
  'display_name',
  'email',
  'first_name',
  'full_name',
  'last_name',
  'mobile',
  'name',
  'nickname',
  'phone',
  'phone_number',
  'telephone',
  'user_email',
  'user_name',
  'username',
])
const PERSON_IDENTITY_KEYS = new Set(['email', 'name', 'nickname', 'username'])

// User-controlled display names add no useful aggregation dimension. Keep the
// stable resource ID and drop these fields instead of trying to retain a
// partially redacted value.
const EVENT_FREE_TEXT_KEYS_TO_DROP = new Set(['app_name', 'avatar_name', 'client_name', 'key_name'])

const CREDENTIAL_PROPERTY_KEYS = new Set([
  'access_token',
  'api_key',
  'apikey',
  'api_token',
  'auth_token',
  'authorization_code',
  'authorization',
  'client_key',
  'client_secret',
  'code_challenge',
  'code_verifier',
  'credential',
  'credentials',
  'id_token',
  'mfa_code',
  'oauth_code',
  'oauth_state',
  'one_time_code',
  'otp',
  'password',
  'private_key',
  'refresh_token',
  'reset_code',
  'secret',
  'session_token',
  'signing_key',
  'token',
  'verification_code',
])

const CREDENTIAL_PROPERTY_SUFFIX_PATTERN = /(?:^|_)(?:access_token|api_key|api_token|auth_token|authorization_code|client_key|client_secret|code_challenge|code_verifier|credential|credentials|id_token|mfa_code|oauth_code|oauth_state|one_time_code|otp|password|private_key|refresh_token|reset_code|secret|session_token|signing_key|token|verification_code)$/i
const CREDENTIAL_LABEL_SOURCE = String.raw`(?:access[\s_-]*token|api[\s_-]*(?:key|token)|auth[\s_-]*token|authorization(?:[\s_-]*code)?|client[\s_-]*(?:key|secret)|code[\s_-]*(?:challenge|verifier)|credential|id[\s_-]*token|mfa[\s_-]*code|oauth[\s_-]*(?:code|state|token)|one[\s_-]*time[\s_-]*code|otp|password|private[\s_-]*key|refresh[\s_-]*token|reset[\s_-]*code|secret|session[\s_-]*token|signing[\s_-]*key|token|verification[\s_-]*code)`
const LABELED_CREDENTIAL_PATTERN = new RegExp(
  String.raw`(^|\W)(["']?${CREDENTIAL_LABEL_SOURCE}["']?(?:\s*[:=]\s*|\s+is\s+))(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[^\s,;&|<>{}"']+)`,
  'gi',
)
const SENSITIVE_QUERY_VALUE_PATTERN = /([?&#](?:access[_-]?token|api[_-]?key|auth[_-]?token|authorization|code|credential|id[_-]?token|key|password|refresh[_-]?token|secret|state|token)=)[^&#\s]+/gi
const OAUTH_CODE_STATE_PATTERN = /(^|\W)(["']?(?:code|state)["']?(?:\s*[:=]\s*|\s+is\s+))(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[^\s,;&|<>{}"']+)/gi
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const UUID_IN_TEXT_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi
// Compact JWS has three base64url segments. Compact JWE has five, and the
// encrypted-key segment is legitimately empty for `alg=dir`. Match the full
// token before the three-segment alternative so no JWE tail can survive.
const COMPACT_TOKEN_PATTERN = /(^|[^\w-])([\w-]{8,}\.[\w-]*\.[\w-]{8,}\.[\w-]*\.[\w-]{8,}|[\w-]{8,}\.[\w-]*\.[\w-]{8,})(?=$|[^\w-])/g
const ANALYTICS_EVENT_NAME_PATTERN = /^\$?[a-z][\w.:-]*$/i
const ANALYTICS_PROPERTY_KEY_PATTERN = /^\$?[a-z][\w.:-]*$/i
const POSTHOG_HEATMAP_EVENT = '$$heatmap'
// posthog-js survey lifecycle events carry a space in their names, which the
// generic event-name contract rejects. Allow exactly these SDK-owned names.
const POSTHOG_SURVEY_EVENT_NAMES = new Set(['survey shown', 'survey sent', 'survey dismissed', 'survey abandoned'])
// The SDK marks a person as done with a survey through
// `$survey_dismissed/<survey id>` / `$survey_responded/<survey id>` person
// properties. Those are what stop a survey from re-appearing across devices.
const POSTHOG_SURVEY_PERSON_KEY_PATTERN = /^\$survey_(?:dismissed|responded)\/[0-9a-f-]{36}$/i
const MAX_ANALYTICS_EVENT_NAME_LENGTH = 160
const MAX_ANALYTICS_PROPERTY_KEY_LENGTH = 200
const UNICODE_EMAIL_PATTERN = /[\p{L}\p{M}\p{N}_.%+-]+@[\p{L}\p{M}\p{N}-]+(?:\.[\p{L}\p{M}\p{N}-]+)+/gu

function isSafeAnalyticsEventName(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > MAX_ANALYTICS_EVENT_NAME_LENGTH)
    return false

  const normalized = value.normalize('NFKC').trim()
  return normalized === value
    && (normalized === POSTHOG_HEATMAP_EVENT || POSTHOG_SURVEY_EVENT_NAMES.has(normalized) || ANALYTICS_EVENT_NAME_PATTERN.test(normalized))
    && sanitizeSensitiveAnalyticsText(normalized, true) === normalized
}

function redactPhoneNumbers(value: string): string {
  return value
    .replace(/\b((?:mobile|phone(?:_number)?|tel(?:ephone)?)\s*(?::|=))[ \t]*\+?\p{Nd}(?:[\s()._/:+-]*\p{Nd}){6,14}/giu, '$1 [PHONE_REDACTED]')
    .replace(/(?<![\p{L}\p{N}])\+\p{Nd}(?:[\s()._/:+-]*\p{Nd}){7,14}(?!\p{Nd})/gu, '[PHONE_REDACTED]')
    .replace(/(?<!\p{Nd})(?:\p{Nd}[\s()._/:+-]*){10,11}(?!\p{Nd})/gu, '[PHONE_REDACTED]')
}

function redactPhoneNumbersPreservingUuids(value: string): string {
  let result = ''
  let lastIndex = 0

  for (const match of value.matchAll(UUID_IN_TEXT_PATTERN)) {
    const index = match.index ?? 0
    result += redactPhoneNumbers(value.slice(lastIndex, index))
    result += match[0]
    lastIndex = index + match[0].length
  }

  return result + redactPhoneNumbers(value.slice(lastIndex))
}

function sanitizeEmbeddedUrl(rawUrl: string): string {
  try {
    const value = rawUrl.trim()
    const isProtocolRelative = value.startsWith('//')
    const absoluteScheme = value.match(/^([a-z][a-z\d+.-]*):/i)?.[1]?.toLowerCase()
    const parsed = new URL(value, 'https://analytics.invalid')
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
      return '[URL_REDACTED]'

    parsed.username = ''
    parsed.password = ''
    parsed.search = ''
    parsed.hash = ''
    parsed.pathname = sanitizeAnalyticsPath(parsed.pathname)

    if (isProtocolRelative)
      return `//${parsed.host}${parsed.pathname}`
    if (!absoluteScheme) {
      if (value.startsWith('/'))
        return parsed.pathname
      return parsed.pathname.replace(/^\//, '')
    }
    return parsed.toString()
  }
  catch {
    return '[URL_REDACTED]'
  }
}

function redactOpaqueCredential(value: string): string {
  if (UUID_PATTERN.test(value))
    return value

  return containsOpaqueAnalyticsToken(value)
    ? '[CREDENTIAL_REDACTED]'
    : value
}

/**
 * Redact credentials and obvious PII from an untrusted analytics string.
 * Safe, low-cardinality dimensions remain unchanged.
 */
export function sanitizeSensitiveAnalyticsText(value: string, redactOpaque = false): string {
  const hasOAuthContext = /\boauth\b/i.test(value)
  let sanitized = value
    .normalize('NFKC')
    .replace(/\p{Cc}/gu, ' ')
    .replace(/https?:\/\/[^\s<>{}|\\^`[\]"']+/gi, sanitizeEmbeddedUrl)
    .replace(/\/\/[^\s<>{}|\\^`[\]"']+/g, sanitizeEmbeddedUrl)
    .replace(SENSITIVE_QUERY_VALUE_PATTERN, '$1[CREDENTIAL_REDACTED]')
    .replace(/\bBearer\s+[^\s,;&|<>{}"']+/gi, 'Bearer [CREDENTIAL_REDACTED]')
    .replace(/\bBasic\s+[^\s,;&|<>{}"']+/gi, 'Basic [CREDENTIAL_REDACTED]')
    .replace(COMPACT_TOKEN_PATTERN, '$1[JWT_REDACTED]')
    .replace(/\b(?:sk|pk)[-_][\w-]{8,}\b/gi, '[API_KEY_REDACTED]')
    .replace(/\b(?:phc|gh[oprsu])_[\w-]{8,}\b/gi, '[API_KEY_REDACTED]')
    .replace(/\bgithub_pat_\w{8,}\b/gi, '[API_KEY_REDACTED]')
    .replace(/\bxox[baprs]-[a-z0-9-]{8,}\b/gi, '[TOKEN_REDACTED]')
    .replace(LABELED_CREDENTIAL_PATTERN, '$1$2[CREDENTIAL_REDACTED]')
    .replace(UNICODE_EMAIL_PATTERN, '[EMAIL_REDACTED]')

  if (redactOpaque) {
    sanitized = sanitized.replace(/(?<![a-z0-9])[\w-]{32,}(?![a-z0-9])/gi, redactOpaqueCredential)
  }

  sanitized = redactPhoneNumbersPreservingUuids(sanitized)

  if (hasOAuthContext)
    sanitized = sanitized.replace(OAUTH_CODE_STATE_PATTERN, '$1$2[CREDENTIAL_REDACTED]')

  return sanitized.trim().slice(0, 1000)
}

function normalizePropertyKey(key: string): string {
  return key
    .replace(/^\$/, '')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .replace(/([a-z\d])([A-Z])/g, '$1_$2')
    .replace(/[^a-z\d]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()
}

function isSafeAnalyticsPropertyKey(key: string, context: PropertyBagContext = 'event'): boolean {
  if (!key || key.length > MAX_ANALYTICS_PROPERTY_KEY_LENGTH)
    return false

  if (context === 'person' && POSTHOG_SURVEY_PERSON_KEY_PATTERN.test(key))
    return true

  if (!ANALYTICS_PROPERTY_KEY_PATTERN.test(key))
    return false

  // Property names can be caller-controlled at nested levels. Treat them as
  // untrusted text too; otherwise an email, phone or compact token can leak as
  // an object key even when every value is sanitized. Stable analytics contract
  // keys use a stable ASCII identifier contract, so encoded, free-text and
  // Unicode dynamic names are rejected before they can become dimensions.
  return sanitizeSensitiveAnalyticsText(key, true) === key
}

function isCredentialPropertyKey(key: string): boolean {
  const normalized = normalizePropertyKey(key)
  return CREDENTIAL_PROPERTY_KEYS.has(normalized) || CREDENTIAL_PROPERTY_SUFFIX_PATTERN.test(normalized)
}

function isUrlPropertyKey(key: string): boolean {
  const normalized = normalizePropertyKey(key)
  return normalized === 'url'
    || normalized === 'uri'
    || normalized.endsWith('_url')
    || normalized.endsWith('_uri')
}

function isPathPropertyKey(key: string): boolean {
  const normalized = normalizePropertyKey(key)
  return normalized === 'path'
    || normalized === 'pathname'
    || normalized === 'route_template'
    || normalized.endsWith('_path')
    || normalized.endsWith('_pathname')
}

function sanitizeHeatmapData(value: unknown): Record<string, unknown[]> | undefined {
  if (!value || Object.prototype.toString.call(value) !== '[object Object]')
    return undefined

  const sanitized: Record<string, unknown[]> = {}
  for (const [rawUrl, rawPoints] of Object.entries(value as Record<string, unknown>)) {
    if (!Array.isArray(rawPoints))
      continue

    const url = sanitizeEmbeddedUrl(rawUrl)
    if (!/^https?:\/\//i.test(url))
      continue

    const points = rawPoints.flatMap((rawPoint) => {
      if (!rawPoint || Object.prototype.toString.call(rawPoint) !== '[object Object]')
        return []

      return [sanitizeAnalyticsProperties(rawPoint as Record<string, unknown>)]
    })

    if (points.length > 0)
      sanitized[url.slice(0, 1000)] = [...(sanitized[url.slice(0, 1000)] ?? []), ...points]
  }

  return Object.keys(sanitized).length > 0 ? sanitized : undefined
}

function sanitizeAnalyticsValue(key: string, value: unknown, context: PropertyBagContext): unknown {
  if (typeof value === 'string') {
    if (context === 'person' && PERSON_IDENTITY_KEYS.has(key))
      return value
    // Stable resource and journey identifiers are UUIDs and are explicitly
    // preserved by the opaque detector. Everything else crosses the final
    // boundary as untrusted text, regardless of which property name carried it.
    return sanitizeSensitiveAnalyticsText(value, true)
  }

  if (Array.isArray(value))
    return value.map(item => sanitizeAnalyticsValue(key, item, context))

  if (value && Object.prototype.toString.call(value) === '[object Object]') {
    // Only CaptureResult.$set/$set_once roots are legitimate person bags;
    // sanitizePostHogCapture passes those roots explicitly as `person`. Any
    // nested object (including a caller-supplied `$set`) returns to event rules.
    return sanitizeAnalyticsProperties(value as Record<string, unknown>, 'event')
  }

  return value
}

/**
 * Final event-boundary property sanitizer. Person identity fields are retained
 * only inside PostHog's explicit $set/$set_once person-property bags.
 */
export function sanitizeAnalyticsProperties(
  propertyBag: Record<string, unknown>,
  context: PropertyBagContext = 'event',
): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(propertyBag)) {
    if (!isSafeAnalyticsPropertyKey(key, context))
      continue

    const normalizedKey = normalizePropertyKey(key)
    if (context === 'event' && (EVENT_PII_PROPERTY_KEYS.has(normalizedKey) || EVENT_FREE_TEXT_KEYS_TO_DROP.has(normalizedKey)))
      continue
    if (isCredentialPropertyKey(key))
      continue

    // URL fields are cleaned here as a last line of defence even if a caller
    // bypasses the normal attribution URL sanitizer.
    if (typeof value === 'string' && isUrlPropertyKey(key)) {
      sanitized[key] = sanitizeEmbeddedUrl(value)
      continue
    }
    if (typeof value === 'string' && isPathPropertyKey(key)) {
      sanitized[key] = sanitizeAnalyticsPath(value)
      continue
    }

    sanitized[key] = sanitizeAnalyticsValue(key, value, context)
  }

  return sanitized
}

/**
 * PostHog before_send hook: first apply URL/attribution rules, then recursively
 * remove credentials and obvious PII from every remaining ordinary property.
 */
export function sanitizePostHogCapture(
  event: CaptureResult | null,
  expectedProjectToken = '',
): CaptureResult | null {
  if (!event || !isSafeAnalyticsEventName(event.event))
    return null

  const sanitizedEvent = sanitizePostHogEvent(event)
  if (!sanitizedEvent)
    return null

  const sanitizedProperties = sanitizeAnalyticsProperties(sanitizedEvent.properties)
  if (sanitizedEvent.event === POSTHOG_HEATMAP_EVENT) {
    const heatmapData = sanitizeHeatmapData(sanitizedEvent.properties.$heatmap_data)
    if (!heatmapData)
      return null
    sanitizedProperties.$heatmap_data = heatmapData
  }
  // PostHog injects its project token into the root transport properties before
  // before_send. Preserve that required SDK field only at this final root; the
  // normal sanitizer still removes caller/nested credential-shaped `token`s.
  if (
    expectedProjectToken
    && sanitizedEvent.properties.token === expectedProjectToken
  ) {
    sanitizedProperties.token = sanitizedEvent.properties.token
  }

  return {
    ...sanitizedEvent,
    properties: sanitizedProperties,
    $set: sanitizedEvent.$set
      ? sanitizeAnalyticsProperties(sanitizedEvent.$set, 'person')
      : undefined,
    $set_once: sanitizedEvent.$set_once
      ? sanitizeAnalyticsProperties(sanitizedEvent.$set_once, 'person')
      : undefined,
  }
}
